import { sleep } from '@cldsystems/e3-fe-lib';
import {
  WebshopFavoritesAddProduct,
  WebshopFavoritesCommands,
  WebshopFavoritesCreate,
  WebshopFavoritesQueries,
  webshopFavoritesAddProductService,
  webshopFavoritesCreateService,
} from '@cldsystems/ws-api-client';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useKeycloak } from '@/providers/keycloak-provider';
import { runtimeHttpClient } from '@/services/services.setup';
import { useStore } from '@/store/zustand/zustand.setup';

interface Props {
  payload: WebshopFavoritesCreate | WebshopFavoritesAddProduct;
  createEnabled: boolean;
  addEnabled: boolean;
  favoritesId: string | undefined;
  favoritesVersion: number | undefined;
}

const useFavoritesSyncQuery = ({
  payload,
  createEnabled,
  addEnabled,
  favoritesId,
  favoritesVersion,
}: Props) => {
  const { keycloak } = useKeycloak();
  const { resetFavorites } = useStore();
  const queryClient = useQueryClient();
  const { company } = useStore();
  const [isSettling, setIsSettling] = useState(false);

  const favoritesCreateQuery = useQuery({
    queryKey: [WebshopFavoritesCommands.Create, keycloak?.idTokenParsed?.sub],
    queryFn: () =>
      webshopFavoritesCreateService({ hasSchema: true, httpClient: runtimeHttpClient })({
        payload: { id: '', ...payload, companyId: company.companyId ?? '' },
      }),
    enabled: createEnabled,
  });

  const favoritesAddQuery = useQuery({
    queryKey: [WebshopFavoritesCommands.AddProduct, keycloak?.idTokenParsed?.sub],
    queryFn: () =>
      webshopFavoritesAddProductService({ hasSchema: true, httpClient: runtimeHttpClient })({
        params: { id: favoritesId!, version: favoritesVersion?.toString() ?? '' },
        payload: {
          productIds: payload.productIds,
          event: {
            name: '',
            id: '',
            sourceUrl: '',
          },
        },
      }),
    enabled: addEnabled,
  });

  useEffect(() => {
    if (favoritesCreateQuery.isSuccess || favoritesAddQuery.isSuccess) {
      setIsSettling(true);
      resetFavorites();
      void (async () => {
        await sleep(1000);
        await queryClient.invalidateQueries({
          queryKey: [WebshopFavoritesQueries.GetMyIdAndVersion],
          refetchType: 'all',
        });
        await queryClient.invalidateQueries({
          queryKey: [WebshopFavoritesQueries.GetById],
          refetchType: 'all',
        });
        setIsSettling(false);
      })();
    }
  }, [favoritesCreateQuery.isSuccess, favoritesAddQuery.isSuccess, resetFavorites, queryClient]);

  return {
    isSyncing: favoritesCreateQuery.isFetching || favoritesAddQuery.isFetching || isSettling,
  };
};

export default useFavoritesSyncQuery;
